import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useOutletContext, useSearchParams } from "react-router-dom";
import { useDebounce } from "react-use";
import EmptyBanner from '../../components/EmptyBanner'
import ErrorBanner from '../../components/ErrorBanner'
// import Switch from "react-switch";
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from "../../providers/AuthProvider";
import InfoTooltip from '../../components/InfoTooltip';
import TalentCandidate from "../../models/TalentCandidate";
import { usePosition } from "../../providers/ProjectPositionProvider";
import { useProject } from "../../providers/ClientProjectProvider";
import CandidateProvider from "../../providers/CandidateProvider";
import { useAPI } from "../../config/API";
import ScopingCallBanner from "./components/ScopingCallBanner";
import Pagination from "../../components/Pagination";
import { Select, Switch } from "antd";
import SearchTalentSidebar from "../talent/SearchTalentSidebar";
import SidebarComponent from "../../components/SidebarComponent";
import CandidateTileRedesign, { CandidateTileRedesignSkeleton } from '../talent/CandidateTileRedesign';

// const DEFAULT_PER_PAGE = 9;
// const DEFAULT_FILTER = listTypes.All;
const PositionMatches = (props) => {
    const { project } = useProject();
    const { position } = usePosition();

    const location = useLocation();
    const {
        captureTalentRequest,
        onRequest,
        listTypes,
        isDisabled
    } = useOutletContext();

    const [searchParams, setSearchParams] = useSearchParams();
    const [inputValue, setInputValue] = useState(searchParams.get('search') || '');

    const [, cancel] = useDebounce(
        () => {
            const params = new URLSearchParams(searchParams);
            if (inputValue.length) {
                params.set('search', inputValue);
            } else {
                params.delete('search');
            }
            params.delete('page');

            return setSearchParams(params, { replace: true });
        }, 600, [inputValue]
    );


    const [DEFAULT_PER_PAGE, setDEFAULT_PER_PAGE] = useState(() => {
        let maxItems = TB.floor(window.innerWidth / 380);
        maxItems = Math.max(maxItems, 1);
        maxItems = Math.min(maxItems, 4);

        if (!maxItems) return 3;
        return maxItems * 3
    });

    const getPerPage = useMemo(() => {
        if (searchParams.has('per_page')) {
            return parseInt(searchParams.get('per_page')) || DEFAULT_PER_PAGE
        }
        return DEFAULT_PER_PAGE;
    }, [searchParams.get('per_page')]);

    const activeListType = useMemo(() => {
        if (searchParams.has('filter')) {
            return TB.startCase(searchParams.get('filter')) || listTypes.All
        }
        return listTypes.All;
    }, [searchParams.toString()]);

    const [sortBy, setSortBy] = useState(() => {
        if (project.matchSort() && TalentCandidate.matchSortingOptions()[project.matchSort()]) {
            return project.matchSort();
        }
        return TalentCandidate.matchSortingOptions()[0]
    });
    const decodedSearchParams = useMemo(() => {
        let decoded = [...searchParams.entries()].reduce((acc, [key, val]) => {
            return { ...acc, [key]: val };
        }, {});
        return TB.pick(decoded, [
            'skills',
            'disciplines',
            'roles',
            'industries',
            'location',
            'latitude',
            'longitude',
            'pool_ids',
            'regions',
            'brands',
            'experience'
        ])
    }, [searchParams?.toString()]);

    const requestParams = useMemo(() => {

        let params = {
            project_position: position?.id,
            // disciplines: TB.map(position?.disciplines, 'id'),
            per_page: getPerPage,
            sort_by: sortBy,
            page: (searchParams.has('page') && !TB.isNaN(parseInt(searchParams.get('page')))) ? parseInt(searchParams.get('page')) : 1
        };

        if (searchParams.get('only_interested') === '1') {
            params.interested = 1;
        }
        // if(captureTalentRequest) {
        params.capture_talent_request = 1;
        // }

        if (searchParams.get('search')?.length) {
            params.search = searchParams.get('search');
        }

        // params.self_disliked = 0;
        // params.self_liked = 0;
        params.disliked = 0;
        params.liked = 0;
        // params.favorite = 0;
        // params.self_liked = 0;

        switch (TB.startCase(searchParams.get('filter')) || listTypes.All) {
            case listTypes.Favorite:
                params.favorite = 1;
                break;
            case listTypes.Liked:
                // params.self_liked = 1;
                params.liked = 1;
                break;
            case listTypes.Archive:
                params.disliked = 1;
                break;
        }

        let filtersToCheck = [
            'skills',
            'roles',
            // 'disciplines',
            'industries',
            // 'location',
            // 'latitude',
            // 'longitude',
            // 'pool_ids',
            'brands',
            // 'regions',
            'experience'
        ];

        TB.map(filtersToCheck, filter => {
            if (decodedSearchParams[filter]) {
                if (filter === 'roles') {
                    let roleIds = JSON.parse(decodedSearchParams[filter]) || [];
                    roleIds = TB.map(roleIds, id => parseInt(id));

                    if (roleIds.length) {
                        let selectedTemplates = TB.filter(position.templates, template => roleIds.includes(template.id));

                        if (selectedTemplates) {
                            let disciplines = TB.flatMap(selectedTemplates, 'disciplines')
                            params[`filters_disciplines`] = TB.map(TB.uniqBy(disciplines, 'id'), 'id');
                        }
                    }
                } else params[`filters_${filter}`] = decodedSearchParams[filter];
            }
        })
        return params
    }, [
        position?.id,
        getPerPage,
        sortBy,
        captureTalentRequest,
        activeListType,
        decodedSearchParams,
        searchParams.toString()
    ]);

    const { auth } = useAuth();
    const queryClient = useQueryClient();

    const {
        data: positionMatches,
        isFetching,
        isInitialLoading: isLoading,
        isRefetching,
        error,
        refetch,
    } = useAPI(['positionMatches', requestParams],
        {
            enabled: !!position?.id && !project.is_direct_hire,
            keepPreviousData: true
        }
    )


    const typeFilterOptions = useMemo(() => {
        return [
            { type: listTypes.All, count: positionMatches?.matches_total || 0 },
            {
                type: listTypes.Liked,
                count: (positionMatches?.liked_total || 0) || (positionMatches?.self_liked_total || 0)
            },
            {
                type: listTypes.Archive,
                count: (positionMatches?.disliked_total || 0) || (positionMatches?.self_disliked_total || 0)
            }
        ];
    }, [
        position?.id,
        positionMatches?.matches_total,
        positionMatches?.liked_total,
        positionMatches?.disliked_total,
        positionMatches?.self_liked_total,
        positionMatches?.self_disliked_total,
        isLoading
    ]);

    const toggleInterestedOnly = async () => {
        let updateParams = new URLSearchParams(searchParams);

        if (searchParams.get('only_interested') !== '1') {
            updateParams.set('only_interested', '1');
        } else {
            updateParams.delete('only_interested')
        }
        updateParams.delete('page')

        await setSearchParams(updateParams, { replace: true })
    }

    const toggleActiveFilter = async (filter) => {

        let updateParams = new URLSearchParams(searchParams);

        if (filter && searchParams.get('filter') !== TB.slugify(filter)) {
            updateParams.set('filter', TB.slugify(filter));
        } else {
            updateParams.delete('filter')
        }
        updateParams.delete('page')

        await setSearchParams(updateParams, { replace: true })
    }

    const refreshTalentFor = async (updatedTalent) => {
        if (!updatedTalent) {
            return;
        }

        let updatedMatches = { ...positionMatches };
        let refresh = false;
        updatedMatches.users = TB.map([...updatedMatches.users], talent => {
            if (talent.id === updatedTalent.id) {
                let match = TalentCandidate.make({ ...talent });
                if (!match.is_archived && updatedTalent.is_archived) {
                    refresh = true;
                }
                if (match.declinedPosition() && !match.is_archived && updatedTalent.is_archived) {
                    updatedMatches.self_disliked_total = (updatedMatches.self_disliked_total || 0) + 1;
                    updatedMatches.disliked_total = (updatedMatches.disliked_total || 0) + 1;
                    refresh = true;
                }
                return updatedTalent;
            }
            return talent;
        });

        if (refresh) {
            return queryClient.invalidateQueries({
                predicate: ({ queryKey }) => {
                    let params = queryKey[1] || {};
                    return queryKey[0] === 'positionMatches' && params.project_position === position.id;
                }
            })
        }
    }


    const canEditProjectAndPositions = auth?.canEditProjectAndPositions(project);

    let shareableCandidates = 0;

    if (positionMatches && positionMatches.total > 0) {
        // Add client favorites to sharable total
        shareableCandidates += positionMatches.favorites_total || 0
        // Add project_position liked candidates to sharable total
        shareableCandidates += (positionMatches.liked_total || 0) || (positionMatches.self_liked_total || 0)
        // Add project_position candidates that have showed interest sharable total
        shareableCandidates += position?.total_interested_candidates || position?.interested_candidates_count || 0
        // Add project_position candidates that have been invited to sharable total
        shareableCandidates += position?.total_selected_candidates || 0
    }
    const canShare = !position?.hasBeenFilled() && shareableCandidates > 0;

    const onShare = e => {
        e.currentTarget.blur();
        let params = new URLSearchParams(searchParams);
        params.set('shareTalentForRole', String(position?.id));
        return setSearchParams(params);
    }
    const onShowProposal = talent => {
        let existingParams = new URLSearchParams(searchParams);
        existingParams.set('tId', talent.id)
        return setSearchParams(existingParams)
    }

    const emptyBannerMessage = useMemo(() => {
        switch (activeListType) {
            case listTypes.Liked:
                return `No talent saved yet.`;
            case listTypes.Archive:
                return `No talent added to archive yet.`;
            case listTypes.All:
            default:
                if (searchParams.get('only_interested') === '1') {
                    return `No matching talent has shown interest yet.`;
                }
                return `No matching talent available.`;
        }
    }, [activeListType]);

    const talentOptions = useMemo(() => {
        return TB.filter(positionMatches?.users || [], t => TB.isDefined(t))
    }, [positionMatches?.users, isLoading, isRefetching, isFetching])


    useEffect(() => {
        let invalidate = false;
        switch (activeListType) {
            case listTypes.All:
                if (TB.filter(talentOptions, ['job_candidate.was_disliked', true]).length) {
                    invalidate = true;
                }
                break;
            case listTypes.Liked:
                if (TB.filter(talentOptions, ['job_candidate.was_liked', false]).length) {
                    invalidate = true;
                }
                break;
            case listTypes.Archive:
                if (TB.filter(talentOptions, ['job_candidate.was_disliked', false]).length) {
                    invalidate = true;
                }
                break;
        }

        if (invalidate && !isFetching) {
            queryClient.invalidateQueries({
                predicate: ({ queryKey }) => {
                    let params = queryKey[1] || {};
                    return queryKey[0] === 'positionMatches' && params.project_position === position.id;
                },
                fetchStatus: 'idle'
            })
        }
    }, [
        position?.id,
        positionMatches?.liked_total,
        positionMatches?.disliked_total,
        positionMatches?.self_liked_total,
        positionMatches?.self_disliked_total,
        activeListType
    ]);

    const positionFilterOptions = useMemo(() => {
        let {
            templates,
            disciplines = [],
            brands,
            skills = [],
            industries = [],
            visible_talent_pools = []
        } = { ...position };

        const expMapping = {
            80: 'junior',
            95: 'intermediate',
            100: 'senior',
        }
        const experienceLevels = TB.map(laravel.talent_experience_levels, (exp, value) => {
            return {
                ...exp,
                value: expMapping[parseInt(value)],
                name: exp.label + " (" + exp.level + ")",
                display: (
                    <span>
                        Min Experience:&nbsp;({exp.level})
                    </span>
                )
            }
        })

        return {
            roleOptions: TB.map([...templates], template => ({ ...template, value: template.id })),
            skillOptions: TB.map([...skills], skill => ({ ...skill, value: skill.id })),
            industryOptions: TB.map([...industries], ind => ({ ...ind, value: ind.id })),
            experienceOptions: TB.filter(experienceLevels, exp => position[`wants_${exp.value}_experience`]),
            brandOptions: TB.map([...brands], brand => ({ ...brand, value: brand.id }))
            // locationOptions: laravel.project_visibility_location_settings,
            // talentOptions: TB.map([...visible_talent_pools], pool => ({...pool, value: pool.id})),
        }
    }, [position?.id]);

    const positionFilterDefaultOptions = useMemo(() => {
        let {
            roles = [],
            skills = [],
            industries = [],
            wants_junior_experience,
            wants_intermediate_experience,
            wants_senior_experience
        } = { ...position };
        let {
            visibility_setting_location,
            visibility_setting_favorites,
            visibility_setting_hired,
            visibility_setting_invited,
            visibility_setting_all_talent,
            visible_talent_pools = [],
            visible_regions,
        } = project;


        return {
            roles: [],
            skills: [],
            industries: [],
            experience: [],
            brands: []
            // disciplines: TB.map([...disciplines], dis => ({...dis, value: dis.id})),
            // skills: TB.map([...skills], skill => ({...skill, value: skill.id})),
            // industries: TB.map([...industries], ind => ({...ind, value: ind.id})),
            // ...getOptionsByExperience(skills, 'skills'),
            // ...getOptionsByExperience(industries, 'industries'),
            // experience : {
            //     junior: wants_junior_experience,
            //     intermediate: wants_intermediate_experience,
            //     senior: wants_senior_experience
            // },
            // pool_ids: TB.map([...visible_talent_pools], pool => pool.id),
            // favorited: (!visibility_setting_all_talent && visibility_setting_favorites) ? 1 : 0,
            // hired: (!visibility_setting_all_talent && visibility_setting_hired) ? 1 : 0,
            // invited: (!visibility_setting_all_talent && visibility_setting_invited) ? 1 : 0,
            // location: visibility_setting_location,
        }
    }, [position?.id]);

    if (!position?.is_visible) {
        return <div />
    }

    return (
        <React.Fragment>
            <SidebarComponent canClose={true} defaultClosed={false}>
                <SearchTalentSidebar
                    forPosition={position?.id}
                    type="talent"
                    enableImportance={false}
                    enableExperience={false}
                    disabledFilters={['TALENT_TYPES', 'LOCATION']}
                    searchEnabled={false}
                    resetOnMount={false}
                    defaultFilters={positionFilterDefaultOptions}
                    availableFilters={positionFilterOptions}
                    footer={(
                        <div className="basis-full rounded-md bg-secondary text-sm self-stretch text-white mt-2 px-2 py-3">
                            These filters were created based upon the requirements indicated for the position. &nbsp;
                            {!position.hasBeenFilled() && (
                                <React.Fragment>
                                    <Link
                                        to={location.pathname.replace(/talent-pool.*$/, `brief/role-requirements/role/${position.id}`)}
                                        className="text-white underline">
                                        Edit Position Requirements
                                    </Link>
                                </React.Fragment>
                            )}
                        </div>
                    )}
                    title={(
                        <div className="flex flex-col gap-4 items-stretch self-stretch">
                            <p className="text-primary font-semibold mb-3 flex-start">Filter Results</p>
                        </div>
                    )}
                />
            </SidebarComponent>


            <div className="flex-1 flex flex-col py-12 pl-10 pr-6">
                {auth.canManageClients() && (
                    <div className="pb-8">
                        <input
                            type="text"
                            className="w-full p-3"
                            placeholder="Search By Name"
                            value={inputValue}
                            disabled={isDisabled || isFetching || isRefetching || isLoading}
                            onChange={({ currentTarget }) => setInputValue(currentTarget.value)}
                        />
                    </div>
                )}

                {!error && !isLoading && (
                    <ScopingCallBanner className="!mb-8" />
                )}
                <div className="position-talent-search flex flex-1 flex-grow">
                    <div className="flex flex-grow flex-col" id={`position-matches-row-${position?.id}`}>
                        <div className="flex flex-row items-stretch gap-6 justify-between">
                            <div className="flex flex-col items-start gap-1 relative max-w-1/2">
                                <p title={position.title}
                                    className="select-none mb-0 font-semibold text-xl line-clamp-2">
                                    {position?.title}
                                </p>

                                {!position.hasBeenFilled() && (
                                    <Link
                                        to={location.pathname.replace(/talent-pool.*$/, `brief/role-requirements/role/${position.id}`)}
                                        className={TB.mapClass('btn btn-link btn-link-dark text-xs p-0', {
                                            'disabled-edit-link': !canEditProjectAndPositions,
                                        })}
                                        onClick={e => {
                                            if (!canEditProjectAndPositions) {
                                                e.preventDefault();
                                                return TB.showPermissionError();
                                            }
                                        }}
                                    >
                                        Edit Position Requirements
                                        {!canEditProjectAndPositions && (
                                            <i className="fa fa-lock ml-1" />
                                        )}
                                    </Link>
                                )}
                            </div>
                            <div className="ml-auto flex justify-end flex-grow self-start flex-row item-center gap-5">

                                <label htmlFor="type-toggle"
                                    className={TB.mapClass("flex flex-row items-center gap-3", {
                                        "pointer-events-none": isLoading
                                    })}>
                                    <span className="whitespace-nowrap text-sm text-[#1F2029]">
                                        Show only interested
                                    </span>
                                    <Switch
                                        onChange={toggleInterestedOnly}
                                        id="type-toggle"
                                        checked={searchParams.get('only_interested') === '1'}
                                    />
                                </label>


                                <TalentFilterTabs
                                    options={typeFilterOptions}
                                    value={activeListType}
                                    isDisabled={isDisabled}
                                    isBusy={isLoading || isFetching || isRefetching}
                                    onChange={toggleActiveFilter}
                                />
                            </div>
                        </div>


                        {error && (
                            <ErrorBanner
                                title="Something went wrong"
                                message="Unable to load talent. Please try again later"
                                onRetry={refetch} />
                        )}


                        {!error && (
                            <React.Fragment>

                                <div className="mt-6 px-3 py-2 bg-white rounded-md">
                                    <div className="flex justify-between flex-row item-center">

                                        <div className="flex flex-row gap-4 item-center">
                                            {!isLoading && canShare && (
                                                <div className="flex flex-row items-center gap-3">
                                                    <button type="button" id="share-btn"
                                                        className="btn btn-primary" onClick={onShare}>
                                                        <i className="fa fa-share mr-2" />
                                                        Share
                                                    </button>
                                                    <InfoTooltip type="dark" target="share-btn">
                                                        Share talent profiles with other team members
                                                    </InfoTooltip>
                                                </div>

                                            )}
                                            <div className="flex flex-row gap-1 items-center">
                                                <p className="text-black font-bold">
                                                    Sort:
                                                </p>
                                                <Select
                                                    defaultValue={TB.first(TB.keys(TalentCandidate.matchSortingOptions()))}
                                                    value={sortBy}
                                                    disabled={isFetching}
                                                    onChange={(sort) => {
                                                        project.setMatchSort(sort);
                                                        let params = new URLSearchParams(searchParams);
                                                        params.delete('page')
                                                        setSearchParams(params);
                                                        return setSortBy(sort)
                                                    }}
                                                    suffixIcon={<i className="text-primary fas fa-chevron-down" />}
                                                    bordered={false}
                                                    options={TB.map(TalentCandidate.matchSortingOptions(), (label, value) => ({
                                                        value,
                                                        label
                                                    }))}
                                                    popupClassName="min-w-[275px] [&_.rc-virtual-list-holder]:!max-h-[unset]"
                                                    getPopupContainer={() => document.getElementById('search-talent-container')}
                                                    className="text-primary font-bold"
                                                    placement="bottomRight"
                                                />
                                            </div>

                                        </div>
                                        <div className="flex flex-row items-center gap-5">

                                            {!isLoading && positionMatches?.current_page && positionMatches?.last_page > 0 && (
                                                <Pagination
                                                    isDisabled={isDisabled || isFetching || isRefetching || isLoading}
                                                    currentPage={positionMatches?.current_page}
                                                    useParams={true}
                                                    pagesCount={positionMatches?.last_page}
                                                    total={positionMatches?.total}
                                                    perPage={positionMatches?.per_page || getPerPage}
                                                />
                                            )}
                                        </div>

                                    </div>

                                </div>
                                <div className="pt-6 flex-grow">
                                    <div className={TB.mapClass("browse-list full-card-list", {
                                        "justify-start": talentOptions?.length > 0
                                    })}>

                                        {isFetching ? (
                                            <div className="pt-6 flex-grow">
                                                <div className="browse-list full-card-list">
                                                    <div className="flex flex-wrap gap-8 justify-center">
                                                        {TB.map([...Array(12).keys()], index => (
                                                            <div className="max-w-[342px] min-w-[342px]"
                                                                key={`placeholder-${index}`}>
                                                                <CandidateTileRedesignSkeleton />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            talentOptions?.length === 0 ? (
                                                <EmptyBanner message={emptyBannerMessage} />
                                            ) : (
                                                <div className="flex flex-wrap gap-8 justify-center">
                                                    {talentOptions.map(talent => (
                                                        <div key={`p${position?.id}-${talent.id}`}
                                                            className="min-w-[342px] max-w-[342px]">
                                                            <CandidateProvider
                                                                candidate={talent}>
                                                                <CandidateTileRedesign
                                                                    hideAvailability={true}
                                                                    candidate={talent}
                                                                    onRequest={onRequest}
                                                                    onUpdated={refreshTalentFor}
                                                                    isDisabled={isDisabled || isLoading || isFetching || isRefetching}
                                                                    placeholder={isFetching}
                                                                />
                                                            </CandidateProvider>
                                                        </div>
                                                    ))}
                                                </div>
                                            )
                                        )}

                                    </div>
                                </div>

                                {!isLoading && positionMatches?.current_page && positionMatches?.last_page > 0 && (
                                    <div className="mb-3 flex flex-col items-end">
                                        <Pagination
                                            isDisabled={isDisabled || isFetching || isRefetching || isLoading}
                                            currentPage={positionMatches?.current_page}
                                            useParams={true}
                                            pagesCount={positionMatches?.last_page}
                                            total={positionMatches?.total}
                                            perPage={positionMatches?.per_page || getPerPage}
                                        />
                                    </div>
                                )}

                            </React.Fragment>
                        )}


                    </div>
                </div>
            </div>


        </React.Fragment>
    );
}

export default PositionMatches

/**
 * Segmented control for the talent list filters.
 *
 * Plain buttons rather than antd's Radio.Group: the target here is a pill
 * group floating on a white track, which meant overriding most of antd's
 * bordered-button styling anyway, and `important: true` makes those overrides
 * a specificity fight.
 *
 * Behaviour is unchanged — one active filter at a time, and a filter with no
 * results stays unselectable.
 */
const TalentFilterTabs = ({ options, value, isDisabled, isBusy, onChange }) => (
    <div
        role="group"
        aria-label="Filter talent"
        aria-busy={!!isBusy}
        className={TB.mapClass(
            'inline-flex items-center gap-1 self-center rounded-full bg-white p-1 shadow-[0_1px_2px_rgba(14,14,18,0.06),0_2px_6px_rgba(14,14,18,0.05)] transition-opacity duration-150',
            { 'opacity-60': isBusy }
        )}
    >
        {TB.map(options, option => {
            const isActive = value === option.type;

            // While a request is in flight the counts on hand belong to the
            // previous filter, so they are worse than nothing — they read as
            // fact. A dash holds the space until the real number lands.
            //
            // Emptiness is unknown mid-request too, so tabs are disabled on
            // `isBusy` rather than on a count that may be about to change.
            const isEmpty = !isBusy && !option.count;

            return (
                <button
                    key={option.type}
                    type="button"
                    aria-pressed={isActive}
                    disabled={isDisabled || isBusy || isEmpty}
                    onClick={() => onChange(option.type)}
                    className={TB.mapClass(
                        // Dimmed whenever it can't be pressed — during a
                        // request as much as when a tab is empty. Without it
                        // the control looks live and clicks go nowhere.
                        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-5 py-2 text-sm font-semibold transition-all duration-150 disabled:pointer-events-none disabled:opacity-40',
                        {
                            'bg-primary text-white shadow-[0_1px_2px_rgba(95,61,177,0.35)]': isActive,
                            'text-[#1F2029] hover:bg-[#F1F1F4]': !isActive,
                        }
                    )}
                >
                    {option.type}
                    <span
                        className={TB.mapClass('tabular-nums', {
                            'animate-pulse': isBusy,
                            'text-white/70': isActive,
                            'text-[#8B8F9A]': !isActive,
                        })}
                    >
                        {isBusy ? '–' : option.count}
                    </span>
                </button>
            );
        })}
    </div>
);